var m = require('mithril');
var go = require('gojs');
var diagramConfiguration = require('./diagramConfiguration');
var edit = require('../../../layout/components/edit');
var editCustomEquipmentModal = require('../../editCustomEquipmentModal');
var editFeedsModal = require('../editFeedsModal');
var updatingGoJs = require('./updatingGoJs');
var loaders = require('../../../layout/components/loaders');
var { bindingChannel } = require('../bindingChannel');

var $ = go.GraphObject.make;
const maxSafeDiagramCoordinate = 1000000;

function isSafeDiagramCoordinate(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && Math.abs(numericValue) <= maxSafeDiagramCoordinate;
}

function readNumericCoordinate(point, primaryKey, fallbackKey) {
  if (!point) return null;
  const value = point[primaryKey] !== undefined && point[primaryKey] !== null
    ? point[primaryKey]
    : point[fallbackKey];
  const numericValue = Number(value);
  return isSafeDiagramCoordinate(numericValue) ? numericValue : null;
}

function readStoredPoint(point) {
  if (Array.isArray(point) && point.length >= 2) {
    if (point[0] === undefined || point[0] === null || point[1] === undefined || point[1] === null) return null;
    const x = Number(point[0]);
    const y = Number(point[1]);
    return isSafeDiagramCoordinate(x) && isSafeDiagramCoordinate(y) ? { x, y } : null;
  }

  if (typeof point === 'string') {
    const parts = point.trim().split(/[\s,]+/).map(Number);
    if (parts.length >= 2 && isSafeDiagramCoordinate(parts[0]) && isSafeDiagramCoordinate(parts[1])) {
      return { x: parts[0], y: parts[1] };
    }
    return null;
  }

  const x = readNumericCoordinate(point, 'x', 'S');
  const y = readNumericCoordinate(point, 'y', 'P');
  if (x === null || y === null) return null;
  return { x, y };
}

function readStoredPointArray(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.h)) return data.h;
  if (data && Array.isArray(data.r)) return data.r;
  return [];
}

function createGoPointList(points) {
  let list = new go.List();
  if (!Array.isArray(points) || points.length < 2) return list;
  points.forEach(point => {
    list.add(new go.Point(point.x, point.y));
  });
  return list;
}

function convertStoredPointsToGoList(data) {
  const points = readStoredPointArray(data)
    .map(readStoredPoint)
    .filter(Boolean);
  return createGoPointList(points);
}

function createLinkTemplate(templateMap, map, useGeoPositioning, submitCallback, options) {
  var disableTopologyEdits = options && options.disableTopologyEdits;
  templateMap.add('', createFeedsLinkTemplate(useGeoPositioning, map,  submitCallback));
  templateMap.add('Contains', createContainsLinkTemplate(useGeoPositioning, map));
  templateMap.add('Controls', createControlsLinkTemplate(useGeoPositioning, map));

  function convertRouteToPoints(route) {
    const convertedPoints = [];
    let zoom = map.getZoom();
    (Array.isArray(route) ? route : []).forEach(p => {
      const lat = readNumericCoordinate(p, 'lat', 'latitude');
      const lng = readNumericCoordinate(p, 'long', 'lng');
      if (lat === null || lng === null) return;
      const pos = map.project([lat, lng], zoom);
      if (!pos || !Number.isFinite(Number(pos.x)) || !Number.isFinite(Number(pos.y))) return;
      convertedPoints.push({ x: pos.x, y: pos.y });
    });
    return createGoPointList(convertedPoints);
  }

  function convertPointsToRoute(points, data, model) {
    let zoom = map.getZoom();
    if (updatingGoJs) {
      return data.route; // no-op
    } else {
      const route = [];
      points.each(p => {
        const ll = map.unproject(L.point(p.x, p.y), zoom);
        route.push({ lat: ll.lat, long: ll.lng  });
      });
      model.set(data, "route", route);
      return route;
    }
  }

  function createContainsLinkTemplate(useGeoPositioning, map) {
    return $(go.Link,
      {
        corner: 5, 
        curve: go.Link.JumpOver,
        adjusting: go.Link.Stretch,
        deletable: false,
        reshapable: true,
        resegmentable: true,
        layerName: "Background",
      },
      new go.Binding("fromNode", "fromNode"),
      new go.Binding("toNode", "toNode"),
      new go.Binding("fromPortId", "fromGoPortId"),
      new go.Binding("toPortId", "toGoPortId"),
      new go.Binding("routing", "routing"),
      new go.Binding("deletable", "", data => {
        return edit.vm.editing() && data.class != 'hasComponent' && data.class != 'Contains';
      }),
      new go.Binding("selectable", "", data => {
        return edit.vm.editing() && data.class != 'hasComponent';
      }),
      new go.Binding("movable", "movable", data => {
        return edit.vm.editing();
      }),
      useGeoPositioning ?
        new go.Binding("points", "route", convertRouteToPoints)//.makeTwoWay(convertPointsToRoute)
      :
        new go.Binding('points', 'points', convertStoredPointsToGoList),
      $(go.Shape, 
        { 
          isPanelMain: true, 
          strokeWidth: 1, 
          strokeDashArray: [6, 6] 
        }, 
        new go.Binding("stroke", "linkColor", function(linkColor) {
          return linkColor;
        })
      )
    )
  }

  function createControlsLinkTemplate(useGeoPositioning, map) {
    return $(go.Link,
        {
          corner: 5,
          curve: go.Curve.Bezier,
          curviness: 20,
          adjusting: go.Link.Stretch,
          reshapable: true,
          resegmentable: true,
          deletable: !disableTopologyEdits,
          cursor: edit.vm.editing() ? "pointer" : "default",
        },
      new go.Binding("fromNode", "fromNode"),
      new go.Binding("toNode", "toNode"),
      new go.Binding("fromPortId", "fromGoPortId"),
      new go.Binding("toPortId", "toGoPortId"),
      new go.Binding("deletable", "", data => {
        return edit.vm.editing() && !disableTopologyEdits;
      }),
      new go.Binding("selectable", "selectable", data => {
        return edit.vm.editing();
      }),
      new go.Binding("movable", "movable", data => {
        return edit.vm.editing();
      }),
      useGeoPositioning ?
          new go.Binding("points", "route", convertRouteToPoints)//.makeTwoWay(convertPointsToRoute)
        :
          new go.Binding('points', 'points', convertStoredPointsToGoList),
      $(go.Shape, 
        {
          isPanelMain: true, 
          strokeWidth: 3, 
          strokeDashArray: [1, 1]
        }, 
        new go.Binding("stroke", "linkColor")
      ),
      $(go.Shape,  // This shape acts as the arrowhead
        {
          toArrow: "Standard",
          stroke: null,
          fill: "black"
        },
        new go.Binding("fill", "linkColor")
      ),
      $(go.TextBlock,  // This is the text label for the link
        {
          alignmentFocus: go.Spot.Center, // Center the text
          segmentOffset: new go.Point(0, -10), // Centers text over the link; adjust Y offset as needed
          font: `500 ${diagramConfiguration.fontSize - 2}pt 'Roboto Flex', monospace`,
          wrap: go.TextBlock.None,
          stroke: "white",
          segmentOrientation: go.Link.OrientUpright,
        },
        new go.Binding("text", "labelText")
      )
    );
  }

  function createFeedsLinkTemplate(useGeoPositioning, map) {
    if (edit.vm.editing()) {
      return $(go.Link,
        {
          corner: 5, 
          curve: go.Link.JumpOver,
          adjusting: go.Link.Stretch,
          reshapable: true,
          resegmentable: true,
          deletable: !disableTopologyEdits,
          layerName: "Background",
          cursor: edit.vm.editing() ? "pointer" : "default",
          contextMenu: disableTopologyEdits ? null : $("ContextMenu",
            $("ContextMenuButton",
              $(go.TextBlock, "Edit Link"),
              { 
                click: (e, obj) => {
                  editFeedsModal.editDialogue(obj.part.data, { submitCallback: submitCallback, delete: true })
                  m.redraw();
                }
              }
            ),
            $("ContextMenuButton",
              $(go.TextBlock, "Copy Bindings"),
              { 
                click: (e, obj) => {
                  window.latestBindingData = null;
                  // Always copy the current node's bindings:
                  window.latestBindingData = {
                    originRid: obj.part.data.key
                  };
                  
                  // Persist in localStorage:
                  localStorage.setItem("gojsBindingClipboard", JSON.stringify(window.latestBindingData));
                  
                  // Broadcast the copy event:
                  bindingChannel.postMessage({
                    type: "copyBinding",
                    data: window.latestBindingData
                  });

                  m.redraw();
                }
              }
            ),
            $("ContextMenuButton",
              new go.Binding("visible", "", () => {
                
                return !window.latestBindingData || Object.keys(window.latestBindingData).length === 0 ? false : true
              }),
              $(go.TextBlock, "Paste Bindings"),
              { 
                click: async (e, obj) => {                  
                  let url = `/api/v1/diagram/${obj.part.data.key.slice(1)}/pastebindings`;
                  await loaders.requestWithLoader({
                    url,
                    method: "PUT",
                    body: {
                      originRid: window.latestBindingData.originRid,
                    }
                  })
                  .then((response) => {
                    return submitCallback();
                  })
                  .catch((error) => {
                    console.error("Error:", error);
                  })
                  .finally(() => {
                    //Clear the clipboard after pasting:
                    window.latestBindingData = null;
                    localStorage.removeItem("gojsBindingClipboard");
                  })
                }
              }
            )
          )
        },
        new go.Binding("fromNode", "fromNode"),
        new go.Binding("toNode", "toNode"),
        new go.Binding("fromPortId", "fromGoPortId"),
        new go.Binding("toPortId", "toGoPortId"),
        new go.Binding("routing", "routing"),
        new go.Binding("deletable", "", data => {
          return edit.vm.editing() && !disableTopologyEdits && data.class != 'hasComponent' && data.class != 'Contains';
        }),
        new go.Binding("selectable", "", data => {
          return edit.vm.editing() && data.class != 'hasComponent';
        }),
        new go.Binding("movable", "movable", data => {
          return edit.vm.editing();
        }),
        useGeoPositioning ?
          new go.Binding("points", "route", convertRouteToPoints)//.makeTwoWay(convertPointsToRoute)
        :
          new go.Binding('points', 'points', convertStoredPointsToGoList),
        $(go.Shape,
          {
            strokeWidth: 2.5
          },
          new go.Binding("fill", "linkColor", function(linkColor) {
            return linkColor ? linkColor : "transparent";
          }), 
          new go.Binding("stroke", "linkColor", function(linkColor) {
            return linkColor ? linkColor : "transparent";
          })
        ),
        $(go.Shape, { toArrow: "Standard", strokeWidth: 2.5 },
          new go.Binding("fill", "linkColor", function(linkColor) {
            return linkColor ? linkColor : "transparent";
          }), 
          new go.Binding("stroke", "linkColor", function(linkColor) {
            return linkColor ? linkColor : "transparent";
          })
        )
      )
    } else {
      return $(go.Link,
        {
          corner: 5, 
          curve: go.Link.JumpOver,
          adjusting: go.Link.Stretch,
          reshapable: true,
          resegmentable: true,
          layerName: "Background",
          selectable: false
        },
        new go.Binding("fromNode", "fromNode"),
        new go.Binding("toNode", "toNode"),
        new go.Binding("fromPortId", "fromGoPortId"),
        new go.Binding("toPortId", "toGoPortId"),
        new go.Binding("routing", "routing"),
        new go.Binding("deletable", "", data => {
          return edit.vm.editing() && !disableTopologyEdits && data.class != 'hasComponent' && data.class != 'Contains';
        }),
        new go.Binding("movable", "movable", data => {
          return edit.vm.editing();
        }),
        useGeoPositioning ?
          new go.Binding("points", "route", convertRouteToPoints)//.makeTwoWay(convertPointsToRoute)
        :
          new go.Binding('points', 'points', convertStoredPointsToGoList),
        $(go.Shape, 
          { 
            isPanelMain: true, 
            strokeWidth: 3, 
            name: "PIPE", 
          }, 
          new go.Binding("strokeDashArray", "dashPattern"),
          new go.Binding("stroke", "linkColor", function(linkColor) {
            return linkColor;
          })
        )
      )
    }
  }
}

createLinkTemplate._private = {
  isSafeDiagramCoordinate,
  readNumericCoordinate,
  readStoredPoint,
  convertStoredPointsToGoList
};

module.exports = createLinkTemplate;
